'use client';

import { Tabs as BaseTabs } from '@base-ui-components/react/tabs';

import { cn } from '../../utils/cn';

type TabValue = string | number;

interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultValue?: TabValue;
  value?: TabValue;
  onValueChange?: (value: TabValue) => void;
}

function Tabs({
  className,
  defaultValue,
  value,
  onValueChange,
  children,
  ...props
}: TabsProps) {
  const rootProps = {
    defaultValue,
    value,
    onValueChange: onValueChange
      ? (val: TabValue) => onValueChange(val)
      : undefined,
    ...props,
  } as React.ComponentProps<typeof BaseTabs.Root>;

  return (
    <BaseTabs.Root className={cn(className)} {...rootProps}>
      {children}
    </BaseTabs.Root>
  );
}

interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {}

function TabsList({ className, ...props }: TabsListProps) {
  return (
    <BaseTabs.List
      className={cn(
        'bg-muted text-muted-foreground inline-flex h-9 items-center justify-center rounded-lg p-1',
        className
      )}
      {...(props as React.ComponentProps<typeof BaseTabs.List>)}
    />
  );
}

interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: TabValue;
}

function TabsTrigger({ className, value, ...props }: TabsTriggerProps) {
  const tabProps = {
    value,
    ...props,
  } as React.ComponentProps<typeof BaseTabs.Tab>;
  return (
    <BaseTabs.Tab
      className={cn(
        'ring-offset-background inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-medium whitespace-nowrap transition-all',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[active]:bg-background data-[active]:text-foreground data-[active]:shadow',
        className
      )}
      {...tabProps}
    />
  );
}

interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: TabValue;
}

function TabsContent({ className, value, ...props }: TabsContentProps) {
  const panelProps = {
    value,
    ...props,
  } as React.ComponentProps<typeof BaseTabs.Panel>;
  return (
    <BaseTabs.Panel
      className={cn(
        'ring-offset-background focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        className
      )}
      {...panelProps}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
export type {
  TabValue,
  TabsProps,
  TabsListProps,
  TabsTriggerProps,
  TabsContentProps,
};
